import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Input, Spin } from 'antd';
import { SplitShell } from '../layout/split/SplitShell';
import styles from '../layout/split/split.module.css';
import { ApiError, accountsApi } from '../api';
import type { ResetTokenStatus } from '@velora/contracts';

/** /reset/:token — single-use password reset. */
export function ResetPage() {
  return <SplitShell><ResetForm /></SplitShell>;
}

function ResetForm() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ResetTokenStatus | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    accountsApi.getResetStatus(token).then(setStatus).catch(() => setStatus({ valid: false, reason: 'not_found', email: null }));
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true); setError(null);
    try {
      await accountsApi.reset(token, { password });
      setDone(true);
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      setError(err instanceof ApiError
        ? (err.body as { message?: string } | null)?.message ?? err.message
        : err instanceof Error ? err.message : String(err));
    } finally { setBusy(false); }
  }

  if (!status) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
    );
  }

  return (
    <div>
      <div className={styles.stepHead}>
        <h2 className={styles.stepTitle}>Choose a new password</h2>
        {status.email && <p className={styles.stepSubtitle}>for <b>{status.email}</b></p>}
      </div>

      {!status.valid ? (
        <Alert type="warning" showIcon
          message={status.reason === 'expired' ? 'This reset link expired.'
            : status.reason === 'consumed' ? 'This reset link has already been used.'
            : 'This reset link is invalid.'}
          description={<Link to="/forgot">Request a fresh one</Link>}
          style={{ marginTop: 22 }} />
      ) : done ? (
        <Alert type="success" showIcon message="Password updated"
          description="Redirecting you to sign in…" style={{ marginTop: 22 }} />
      ) : (
        <form className={styles.fields} onSubmit={submit} style={{ marginTop: 22 }}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="reset-pw">New password</label>
            <Input.Password id="reset-pw" size="large" autoComplete="new-password"
              placeholder="min 8 characters" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="reset-confirm">Confirm</label>
            <Input.Password id="reset-confirm" size="large" autoComplete="new-password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          {error && <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />}
          <Button type="primary" htmlType="submit" size="large" block loading={busy}
            disabled={password.length < 8 || password !== confirm}>
            Set password
          </Button>
        </form>
      )}
    </div>
  );
}
